<template>
  <div
    class="tags-popover-wrap"
    @mousedown="focusElementHandler"
    @mouseup="blurElementHandler"
    @keydown.esc.stop="handleBackButtonClick"
  >
    <div
      class="edit-tag-name usn"
      @click.stop="focusInput"
    >
      <input
        ref="tagNameInput"
        v-model="inputValue"
        class="edit-tag-name-input"
        type="text"
        :placeholder="translate('editTagName')"
        @keyup.enter.prevent.stop="handleEnter"
      />
    </div>
    <div class="buttons-colors-wrap">
      <div class="select-tag-color-wrap usn">
        <color-picker
          :selected-color="selectedColor"
          @selectColor="selectColor"
        />
      </div>
      <div class="edit-tag-buttons-wrap">
        <ButtonCustom
          :text="translate('saveChanges')"
          :type="'important'"
          :button-dimensions="{ width: 234, height: 28 }"
          :class="{ disabled: disableButton }"
          @click="saveTag"
        />
        <div
          v-tooltip="{
            content: translate('deleteTag'),
            placement: 'bottom',
          }"
          class="tag-delete-icon-wrap pt"
          @click.stop="deleteTag"
        >
          <em
            :style="getCustomIconStyle(iconPath('trash'))"
            class="tag-delete-icon"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonCustom from '@/components/elements/ButtonCustom';
import cardTagsMixin from '@/mixins/cardTagsMixin';
import deleteTagFromCardMixin from '@/mixins/deleteTagFromCardMixin';
import customIconsMixin from '@/mixins/customIconsMixin';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import ColorPicker from '@/components/widgets/ColorPicker';

export default {
  name: 'CardTagsEditPopup',
  components: {
    ButtonCustom,
    ColorPicker,
  },
  mixins: [cardTagsMixin, deleteTagFromCardMixin, customIconsMixin, elementFocusMixin],
  inject: {
    currentCardId: {
      type: String,
      required: false,
      default: '',
    },
  },
  props: {
    cardId: {
      type: String,
      required: true,
    },
  },
  emits: ['showViewTagPopup', 'closePopup'],
  data() {
    return {
      inputValue: '',
      selectedColor: '#498CF7',
    };
  },
  computed: {
    editTag() {
      return Object.keys(this.tagForEdit).length > 0;
    },

    tagForEdit() {
      return this.$store.getters.getCurrentTagForEdit;
    },

    currentCard() {
      return this.$store.getters.getCardById(this.currentCardId || this.cardId);
    },

    existingTag() {
      return this.projectWorkspaceTags.some(
        (i) =>
          this.tags[i].name.toLowerCase() === this.inputValue.toLowerCase() &&
          this.tags[i].id !== this.tagForEdit.id
      );
    },

    disableButton() {
      return this.existingTag || !this.inputValue;
    },

    isAddedToCurrentCard() {
      return this.currentCard.tags.some((t) => t.id === this.tagForEdit.id);
    },
    cards() {
      return this.$store.getters.getCards;
    },
  },
  mounted() {
    this.$nextTick(() => this.focusInput());

    if (this.editTag) {
      this.inputValue = this.tagForEdit.name;
      this.selectedColor = this.tagForEdit.color;
    }
  },
  methods: {
    focusInput() {
      setTimeout(() => {
        this.$refs.tagNameInput?.focus();
      }, 50);
    },
    closePopup(willOpenViewTagPopup = false) {
      if (this.isFocusElement()) return;
      this.$store.dispatch('setCurrentTagForEdit', {});

      if (willOpenViewTagPopup) {
        this.$emit('showViewTagPopup');
      } else {
        this.$emit('closePopup');
      }
    },
    handleEnter() {
      if (this.editTag) {
        this.saveTag();
      }
    },
    saveTag() {
      if (!this.inputValue || !this.selectedColor) return;
      if (this.existingTag) {
        this.$store.dispatch('addUiNotification', {
          message: this.translate('tagExists'),
          status: this.$constants.uiNotificationStatuses.error,
          duration: 4000,
        });
        return;
      }
      const oldTag = { ...this.tagForEdit };
      const newTag = { name: this.inputValue, color: this.selectedColor };
      if (oldTag.name === newTag.name && oldTag.color === newTag.color) {
        this.handleBackButtonClick();
        return;
      }
      this.$store.dispatch('updateLocalTag', {
        tagId: this.tagForEdit.id,
        tag: { ...newTag, id: this.tagForEdit.id },
        cardId: this.currentCard.id,
      });
      this.$store
        .dispatch('updateTag', { tagId: this.tagForEdit.id, tag: newTag })
        .then(({ tag }) => {
          this.$store.dispatch('updateLocalTag', {
            tagId: tag.id,
            tag,
            cardId: this.currentCard.id,
          });
        })
        .catch(() => {
          this.$store.dispatch('updateLocalTag', {
            tagId: oldTag.id,
            tag: oldTag,
            cardId: this.currentCard.id,
          });
        });
      this.$store.dispatch('setCurrentTagForEdit', {});
      this.handleBackButtonClick();
    },
    deleteTag() {
      const oldTag = { ...this.tagForEdit };
      let restoreTags = null;
      const boardId = this.currentCard.board_id;
      const tagId = this.tagForEdit.id;

      if (this.isAddedToCurrentCard) {
        restoreTags = [...this.currentCard.tags];
        this.localDeleteTagFromCard(tagId);
      }
      // delete local tag
      this.$store.dispatch('deleteLocalTag', { tagId });

      // dispatch api request
      this.$store.dispatch('deleteTag', { boardId, tagId }).catch(() => {
        // restore tag
        this.$store.dispatch('addLocalTag', { tag: oldTag });
        if (restoreTags !== null) {
          this.$store.dispatch('localUpdateCardField', {
            cardId: this.currentCard.id,
            fieldName: 'tags',
            fieldValue: restoreTags,
          });
        }
      });
      this.$store.dispatch('setCurrentTagForEdit', {});

      this.$store.dispatch('bulkDeleteTagFromCards', { tagId });
      this.handleBackButtonClick();
    },
    handleBackButtonClick() {
      this.closePopup(true);
    },
  },
};
</script>

<style lang="scss">
.tags-popover-wrap {
  outline: none;

  .edit-tag-name {
    @apply px-4 py-2 cursor-text;

    .edit-tag-name-input {
      @apply text-text-default bg-important-bg border-none outline-none text-sm w-full tracking-[0.35px];
    }
  }

  .edit-tag-buttons-wrap {
    @apply mt-4;

    .button-wrap {
      @apply rounded;
    }

    .tag-delete-icon-wrap {
      @apply flex rounded p-[5px] border-0 border-solid border-important-border bg-error-600 hover:bg-error-700;

      .tag-delete-icon {
        @apply h-4 w-4 min-w-4 max-w-4 overflow-hidden bg-white;
      }
    }
  }
}
</style>
